import { SPEECH_SAMPLE_RATE, speechLanguageSchema, TRANSCRIPT_MAX } from '@hugo/core'
import { z } from 'zod'

/**
 * hugo speech protocol v1: what a client and a speech service say to each other over a
 * WebSocket. The client offers the subprotocol `hugo-speech.v1` and its token as a second one
 * (`token.<token>`), so the token never shows in a URL. Then:
 * - client → service: `start` (JSON), audio as binary frames (16 kHz mono PCM16, little-endian),
 *   `stop` (JSON) when the user is done;
 * - service → client: `ready`, `partial` transcripts while listening, one `final` (the whole
 *   utterance; empty when nothing was understood), or an `error`; then it closes.
 * The fake speech service speaks it; a real service gets an adapter in the client.
 */
export const SPEECH_PROTOCOL = 'hugo-speech.v1'
/** The prefix of the subprotocol that carries the token. */
export const SPEECH_TOKEN_PROTOCOL_PREFIX = 'token.'

export const speechClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('start'),
    sampleRate: z.literal(SPEECH_SAMPLE_RATE),
    encoding: z.literal('pcm16'),
    language: speechLanguageSchema,
  }),
  z.object({ type: z.literal('stop') }),
])
export type SpeechClientMessage = z.output<typeof speechClientMessageSchema>

export const speechErrorCodes = ['unauthorized', 'bad_request', 'timeout', 'internal'] as const

export const speechServerMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ready') }),
  z.object({ type: z.literal('partial'), text: z.string().max(TRANSCRIPT_MAX) }),
  z.object({ type: z.literal('final'), text: z.string().max(TRANSCRIPT_MAX) }),
  z.object({
    type: z.literal('error'),
    code: z.enum(speechErrorCodes),
    message: z.string().max(300),
  }),
])
export type SpeechServerMessage = z.output<typeof speechServerMessageSchema>
